import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SlideContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
}));

const SlideImage = styled(motion.img)({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
});

const LogoImage = styled(motion.img)({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  position: 'absolute',
  top: 0,
  left: 0,
});

// Transitions for the slides
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.8,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      x: { type: 'spring', stiffness: 300, damping: 30 },
      opacity: { duration: 0.5 },
      scale: { duration: 0.5 }
    }
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.8,
    transition: {
      x: { type: 'spring', stiffness: 300, damping: 30 },
      opacity: { duration: 0.5 },
      scale: { duration: 0.5 }
    }
  })
};

// Transitions for the logo
const logoVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      duration: 0.8,
      ease: "easeInOut"
    }
  },
  exit: { 
    opacity: 0, 
    scale: 1.1,
    transition: { 
      duration: 0.8,
      ease: "easeInOut"
    }
  }
};

// Component for logo and image slideshow with beautiful transitions
const LogoSlideshow = ({ 
  logoInterval = 5000, 
  slideInterval = 5000, 
  height = 400,
  logoScale = 1,
  transparentLogoBackground = true
}) => {
  // Состояния для слайдшоу
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [showLogo, setShowLogo] = useState(true);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  
  // Состояния для логотипа
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  
  // Состояния для кэширования
  const cachedLogoRef = useRef(null); // Ссылка на кэшированный логотип
  const cachedImagesRef = useRef({}); // Ссылки на кэшированные изображения
  
  // Функция для кэширования изображения в памяти браузера
  const cacheImage = (url) => {
    return new Promise((resolve, reject) => {
      // Если изображение уже в кэше, вернуть его
      if (cachedImagesRef.current[url]) {
        resolve(cachedImagesRef.current[url]);
        return;
      }
      
      // Создаем новый объект Image для загрузки
      const img = new Image();
      
      img.onload = () => {
        // Сохраняем изображение в кэше
        cachedImagesRef.current[url] = img;
        resolve(img);
      };
      
      img.onerror = () => {
        console.error(`Failed to load image: ${url}`);
        reject(new Error(`Failed to load image: ${url}`));
      };
      
      // Запускаем загрузку
      img.src = url;
    });
  };
  
  // Функция для проверки существования изображения
  const checkImageExists = async (url) => {
    try {
      await cacheImage(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Загрузка и кэширование слайдов с удаленного VPS
  useEffect(() => {
    const fetchAndCacheSlides = async () => {
      try {
        setLoading(true);
        
        // Используем эндпоинт /api/settings/slides для получения слайдов с удаленного VPS
        const response = await axios.get('/api/settings/slides');
        
        // Проверяем успешность ответа и наличие данных
        if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
          // Получаем массив слайдов с VPS
          const slidesFromVPS = response.data.data;
          console.log('Slides from VPS:', slidesFromVPS);
          
          // Предварительно загружаем и кэшируем все изображения
          const cachePromises = slidesFromVPS.map(async (slide) => {
            try {
              // Получаем URL изображения в зависимости от структуры данных
              const imageUrl = slide.image || slide.imageUrl || slide.url || (typeof slide === 'string' ? slide : null);
              
              if (!imageUrl) {
                console.error('No image URL found in slide:', slide);
                return null;
              }
              
              // Кэшируем изображение
              await cacheImage(imageUrl);
              return imageUrl;
            } catch (error) {
              console.error(`Failed to cache image:`, error);
              return null; // Пропускаем недоступные изображения
            }
          });
          
          // Ждем завершения всех загрузок
          const cachedSlides = await Promise.all(cachePromises);
          
          // Фильтруем недоступные изображения
          const validSlides = cachedSlides.filter(slide => slide !== null);
          
          // Устанавливаем только существующие изображения
          setImages(validSlides);
        } else {
          // Если не удалось получить слайды с VPS, пробуем локальный эндпоинт
          console.log('No slides from VPS, trying local endpoint');
          const localResponse = await axios.get('/api/slides');
          
          if (localResponse.data && Array.isArray(localResponse.data)) {
            // Предварительно загружаем и кэшируем все изображения
            const localCachePromises = localResponse.data.map(async (slide) => {
              try {
                // Получаем URL изображения в зависимости от структуры данных
                const imageUrl = slide.image || slide.imageUrl || (typeof slide === 'string' ? slide : null);
                
                if (!imageUrl) {
                  console.error('No image URL found in local slide:', slide);
                  return null;
                }
                
                // Кэшируем изображение
                await cacheImage(imageUrl);
                return imageUrl;
              } catch (error) {
                console.error(`Failed to cache local image:`, error);
                return null;
              }
            });
            
            // Ждем завершения всех загрузок
            const localCachedSlides = await Promise.all(localCachePromises);
            
            // Фильтруем недоступные изображения
            const localValidSlides = localCachedSlides.filter(slide => slide !== null);
            
            // Устанавливаем только существующие изображения
            setImages(localValidSlides);
          } else {
            setImages([]);
          }
        }
      } catch (err) {
        console.error('Error fetching slides:', err);
        setFetchError('Не удалось загрузить слайды');
        setImages([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAndCacheSlides();
  }, []);
  
  // Загружаем и кэшируем логотип
  useEffect(() => {
    // Функция для загрузки и кэширования логотипа
    const fetchAndCacheLogo = async () => {
      try {
        // Если логотип уже закэширован, используем его
        if (cachedLogoRef.current) {
          setLogoUrl(cachedLogoRef.current.src);
          setLogoLoaded(true);
          return;
        }
        
        // Используем эндпоинт /api/settings/logo для получения логотипа
        const response = await axios.get('/api/settings/logo');
        
        if (response.data && response.data.success && response.data.data) {
          const logoUrl = response.data.data;
          
          // Кэшируем логотип
          const cachedLogo = await cacheImage(logoUrl);
          cachedLogoRef.current = cachedLogo;
          
          // Устанавливаем URL логотипа
          setLogoUrl(logoUrl);
          setLogoLoaded(true);
        } else {
          // Если логотип не найден, используем локальный логотип
          const localLogoUrl = '/assets/logo/logo.png';
          const cachedLogo = await cacheImage(localLogoUrl);
          cachedLogoRef.current = cachedLogo;
          
          setLogoUrl(localLogoUrl);
          setLogoLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching or caching logo:', error);
        
        // В случае ошибки используем локальный логотип
        try {
          const localLogoUrl = '/assets/logo/logo.png';
          const cachedLogo = await cacheImage(localLogoUrl);
          cachedLogoRef.current = cachedLogo;
          
          setLogoUrl(localLogoUrl);
          setLogoLoaded(true);
        } catch (fallbackError) {
          console.error('Failed to load fallback logo:', fallbackError);
        }
      }
    };
    
    // Загружаем и кэшируем логотип
    fetchAndCacheLogo();
    
    // Слушаем событие обновления логотипа
    const handleLogoUpdate = async (event) => {
      if (event.detail && event.detail.logo) {
        try {
          // Кэшируем новый логотип
          const cachedLogo = await cacheImage(event.detail.logo);
          cachedLogoRef.current = cachedLogo;
          
          // Устанавливаем URL нового логотипа
          setLogoUrl(event.detail.logo);
          setLogoLoaded(true);
        } catch (error) {
          console.error('Error caching updated logo:', error);
        }
      }
    };
    
    window.addEventListener('logoUpdated', handleLogoUpdate);
    
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
    };
  }, []);

  // Завершаем загрузку после получения изображений
  useEffect(() => {
    // Если изображения загружены или их нет, завершаем загрузку
    if (images !== null) {
      setLoading(false);
    }
  }, [images]);

  // Управление слайдшоу
  useEffect(() => {
    // Проверяем, есть ли доступные изображения для показа
    const hasValidImages = images && images.length > 0;
    
    // Если нет изображений или идет загрузка, всегда показываем логотип
    if (loading || !hasValidImages) {
      setShowLogo(true);
      return;
    }

    // Настраиваем слайдшоу только если есть изображения
    const interval = setInterval(() => {
      if (showLogo) {
        // Переключаемся с логотипа на следующее изображение
        setShowLogo(false);
        setTimeout(() => {
          setDirection(1);
        }, 100);
      } else {
        // После показа изображения возвращаемся к логотипу
        setShowLogo(true);
        
        // Подготавливаем следующий индекс изображения
        setTimeout(() => {
          // Всегда переходим к следующему изображению
          setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 500);
      }
    }, showLogo ? logoInterval : slideInterval);

    return () => clearInterval(interval);
  }, [showLogo, images, logoInterval, slideInterval, loading]);
  
  // Эффект для отслеживания изменения изображений (без логирования)
  useEffect(() => {
    // Пустой эффект для отслеживания изменения индекса изображения
    // Ранее здесь был лог, который мы удалили
  }, [currentIndex, showLogo, images]);

  if (loading) {
    return (
      <SlideContainer sx={{ height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} thickness={4} />
      </SlideContainer>
    );
  }

  return (
    <SlideContainer sx={{ 
      height,
      backgroundColor: showLogo && transparentLogoBackground ? 'transparent' : 'inherit',
      transition: 'background-color 0.8s ease-in-out',
      position: 'relative'
    }}>
      {/* 2GIS Widget - независимый от других элементов */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        left: 10, 
        zIndex: 100, 
        overflow: 'hidden',
        borderRadius: '8px',
        '& iframe': {
          borderRadius: '8px'
        }
      }}>
        <iframe 
          frameBorder="0" 
          width="114px" 
          height="28px" 
          src="https://widget.2gis.ru/api/widget?org_id=70000001059707489&amp;branch_id=70000001059707490&amp;size=small&amp;theme=dark"
          title="2GIS Widget"
          style={{ borderRadius: '8px' }}
        />
      </Box>
      <AnimatePresence initial={false} custom={direction}>
        {/* Всегда показываем логотип, если showLogo или нет изображений */}
        {(showLogo || !images || images.length === 0) ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: transparentLogoBackground ? 'transparent' : 'inherit',
              zIndex: 10
            }}
          >
            <motion.div
              key="logo"
              variants={logoVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ 
                transform: `scale(${logoScale})`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              {!logoLoaded ? (
                <CircularProgress size={40} thickness={4} />
              ) : logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt="DomikTyumen Logo"
                  sx={{
                    maxWidth: '80%',
                    maxHeight: '80%',
                    objectFit: 'contain',
                    padding: 3
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 3,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.4)'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: theme => theme.palette.primary.main,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '2.5rem',
                      marginBottom: 2
                    }}
                  >
                    D
                  </Box>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      fontWeight: 700,
                      color: theme => theme.palette.primary.main,
                      textAlign: 'center',
                      marginBottom: 1
                    }}
                  >
                    DomikTyumen
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                      color: theme => theme.palette.text.secondary,
                      textAlign: 'center'
                    }}
                  >
                    Аренда недвижимости в Тюмени
                  </Typography>
                </Box>
              )}
            </motion.div>
          </Box>
        ) : (
          <SlideImage
            key={currentIndex}
            src={images[currentIndex]}
            alt={`Slide ${currentIndex + 1}`}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            onError={(e) => {
              console.error(`Failed to load slide image: ${images[currentIndex]}`);
              e.target.src = '/assets/placeholder.jpg'; // Заменяем на плейсхолдер при ошибке
            }}
          />
        )}
      </AnimatePresence>
    </SlideContainer>
  );
};

export default LogoSlideshow;
